import { type ReactNode, useRef, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, type DropdownProps, DropdownToggle } from 'react-bootstrap';
import styled from 'styled-components';
import type { StrictOmit } from 'types/utils';

interface Props extends StrictOmit<DropdownProps, 'show' | 'onToggle'> {
  openOnHover?: true;
  disabled?: boolean;
}

export function DropdownWrapper({ openOnHover, disabled = false, ...props }: Props) {
  const [open, setOpen] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current !== null) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleDropdownMouseEnter = () => {
    clearHoverTimeout();
    setOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    clearHoverTimeout();
    const HOVER_CLOSE_DELAY = 100;
    hoverTimeoutRef.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY);
  };

  const handleDropdownToggle = () => setOpen(isOpen => !isOpen);

  const canMouseHandle = openOnHover && !disabled;

  return (
    <Dropdown
      show={open}
      onToggle={handleDropdownToggle}
      onMouseEnter={canMouseHandle ? handleDropdownMouseEnter : undefined}
      onMouseLeave={canMouseHandle ? handleDropdownMouseLeave : undefined}
      {...props}
    />
  );
}

export const HeightLimitedDropdownMenu = styled(DropdownMenu)`
  max-height: 50vh;
  overflow-y: auto;
`;

interface SpDropdownItemProps {
  active: boolean;
  children: ReactNode;
  disabled?: boolean;
  onClick(): void;
}

export function SpDropdownItem({ active, ...props }: SpDropdownItemProps) {
  return (
    <DropdownItem
      active={active}
      role="option"
      aria-checked={active}
      aria-selected={active}
      className="d-flex align-items-center"
      {...props}
    />
  );
}
export const SpDropdownToggle = styled(DropdownToggle)<{ caret?: boolean }>`
  &&:after {
    ${({ caret = true }) => !caret && 'display:none;'}
  }
`;
